'use client';
import { resolveImageAlt } from '@eliadmin/core/src/utils/image-alt-utils';


import Image from 'next/image';

import type { Category } from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchProducts } from '@eliadmin/core/src/api/products/product-api';
import { buildProductsQuery } from '@eliadmin/core/src/utils/product-utils';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { Breadcrumbs } from '@eliadmin/core/src/components/homepage/breadcrumbs';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Paragraph } from '@eliadmin/core/src/components/ui/paragraph';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { Heading } from '@eliadmin/core/src/components/ui/heading';
import {
  ProductCard,
  ProductCardSkeleton,
} from '@eliadmin/core/src/components/product/product-card';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

const COLLECTION_PRODUCTS_LIMIT = 10;

export function CollectionPage({ collection, locale }: { collection: Category; locale: string }) {
  const { t } = useTranslations();
  const { me, isInitialized } = useCustomerStore();
  const authToken = me?.authToken;

  const query = useMemo(
    () =>
      buildProductsQuery({
        categories: [collection.id],
        order: 'asc',
        orderBy: 'position',
        results: COLLECTION_PRODUCTS_LIMIT,
      }),
    [collection.id],
  );

  const { data: products, isLoading } = useQuery({
    queryKey: [CacheKey.PRODUCTS, 'collection', collection.id, locale, authToken ?? null],
    queryFn: () => fetchProducts({ locale, authToken, query }),
    enabled: isInitialized,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5,
  });

  const productList = products?.data ?? [];

  return (
    <section
      className={cn(
        'custom-section',
        'gap-8 pb-16 pt-8 sm:gap-10 sm:pb-20 sm:pt-10 lg:gap-12 lg:pb-24 lg:pt-12',
      )}
    >
      <Breadcrumbs
        breadcrumb={{ name: collection.name, slug: collection.slug }}
        className={{
          wrapper: 'w-full items-center justify-center',
          list: 'w-full items-center justify-center',
        }}
      />
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-8 sm:gap-10 md:flex-row md:items-center md:justify-between lg:gap-12',
        )}
      >
        <div className="relative h-[243px] w-full overflow-hidden rounded-[4px] sm:h-[330px] md:h-[460px] md:max-w-[676px]">
          <Image
            src={collection?.image?.url ?? '/images/placeholder.png'}
            alt={resolveImageAlt(collection?.image, collection?.name)}
            fill
            className="absolute inset-0 z-0 h-full w-full object-cover object-center"
          />
        </div>
        <div className="flex w-full flex-col items-start justify-start gap-3 sm:gap-4 md:max-w-[676px] lg:gap-6">
          <h1 className={cn('text-center font-bold tracking-tight text-black', 'h1')}>
            {collection?.name}
          </h1>
          <Paragraph className="w-full" size="lg" innerHtml={collection?.short_description} />
        </div>
      </div>
      <div className={cn('custom-container', 'h-[1px] bg-[#666666]')} />
      <Heading center title={t('text3902')} />
      <div
        className={cn(
          'custom-container',
          'grid grid-cols-2 gap-x-2 gap-y-4 sm:grid-cols-3 md:gap-x-3 md:gap-y-6 lg:grid-cols-4 xl:grid-cols-5',
        )}
      >
        {isLoading ? (
          <ProductCardSkeleton amount={COLLECTION_PRODUCTS_LIMIT} variant="vertical" />
        ) : productList.length ? (
          productList.map(product => <ProductCard product={product} key={product.slug} />)
        ) : (
          <Paragraph
            size="lg"
            color="muted-foreground"
            className="col-span-full mb-4 mt-12 text-center"
          >
            {t('text3903')}
          </Paragraph>
        )}
      </div>
    </section>
  );
}
